import { MatchingBreakdownCounted, MatchingGrade, MatchingRuleKind } from '@app/settings/types';

// Verbatim against `apps/backend/prisma/schema.prisma:800-804`.
export enum MatchingRunStatus {
  pending = 'pending',
  completed = 'completed',
  failed = 'failed',
}

// The run screen's own machine — `results` is a screen state, not a run status.
export enum MatchingRunScreenState {
  running = 'running',
  failed = 'failed',
  results = 'results',
}

// Mirrors the backend `MatchingTierEnum`; the FE has no shared enum registry with `apps/backend`.
export enum MatchingTier {
  vip = 'vip',
  pmp = 'pmp',
  // SAA-184 — WCS-only tier name; ISC's tier order never references it.
  expedited = 'expedited',
  standard = 'standard',
}

export enum MatchingSortMode {
  date = 'date',
  score = 'score',
}

// `entity` discriminator on `result.entityDataGaps[]` / `pairs[].dataGaps[]` (02 CONTRACT §6b).
export enum MatchingDataGapEntity {
  gc = 'gc',
  ip = 'ip',
}

export interface MatchingRun {
  runId: string;
  status: MatchingRunStatus;
  result: Record<string, unknown> | null;
}

// `MatchingResultPair.breakdown[]` row (02 CONTRACT §6d.4). Unlike `skipped[].reason` below,
// `grade`'s wire values already match `MatchingGrade` — no mapping needed.
export interface MatchingResultBreakdownRow {
  ruleId: string;
  name: string;
  kind: MatchingRuleKind;
  grade: MatchingGrade;
  weight: number;
  pointsEarned: number;
  counted?: MatchingBreakdownCounted | null;
}

// `MatchingResultPair.skipped[]` row. `reason` is the engine's raw snake_case wire value — map it
// at the view-model boundary (`build-justification-view.ts`), never here.
export interface MatchingResultSkippedRule {
  ruleId: string;
  name: string;
  reason: string;
}

// The `result` blob (`GraphQLJSON` on the wire, so untyped end to end). Hand-written against 02's
// `composeResult` (`matching-result.service.ts:99-201`) — no codegen, so a renamed backend field
// arrives `undefined` at runtime with no compile error; `build-results-view.ts` is defensive for
// exactly that reason.
export interface MatchingResultPair {
  gcId: number;
  ipId: number;
  score: number;
  summary: string;
  pros: string[];
  cons: string[];
  hardFails: string[];
  // Advisory and deliberately unvalidated server-side — any shape, read only for emptiness.
  dataGaps?: unknown;
  // Set by an exclusion rule ("this GC/IP already declined"); such a pair is returned for coverage
  // but must never be rendered.
  excluded?: { ruleId: string; name: string; reason: string } | null;
  weightApplied?: number | null;
  breakdown?: MatchingResultBreakdownRow[] | null;
  skipped?: MatchingResultSkippedRule[] | null;
}

export interface MatchingResultGc {
  id: number;
  displayName: string;
  surroAvailableInDatabase: string | null;
  priorityTags: string[];
}

export interface MatchingResultIp {
  id: number;
  displayName: string;
  startSurroSearch: string | null;
  tier: MatchingTier;
}

export interface MatchingResultPayload {
  pairs: MatchingResultPair[];
  gcs: MatchingResultGc[];
  ips: MatchingResultIp[];
  // Really the full pair objects (`assignProposals` returns `ValidatedPair[]`); only these two
  // fields are read, and a structural subset keeps the FE indifferent to the rest.
  proposals: { gcId: number; ipId: number }[];
  entityDataGaps?: unknown;
}

// Mirrors `apps/backend/apps/api/src/match-confirmation/enums/*.ts` verbatim (no FE↔BE enum
// registry — codestyle.md).
export enum ProfilePdfLookupStatusEnum {
  confirmed = 'confirmed',
  review = 'review',
  ambiguous = 'ambiguous',
  none = 'none',
  unavailable = 'unavailable',
}

export enum MatchConfirmationStepEnum {
  ipFieldAppend = 'ipFieldAppend',
  gcFieldAppend = 'gcFieldAppend',
  followUpTask = 'followUpTask',
}

export enum MatchConfirmationStepStatusEnum {
  pending = 'pending',
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface MatchConfirmationFolder {
  name: string;
  score: number;
}

export interface MatchConfirmationCandidate {
  fileId: string;
  fileName: string;
  folderName: string;
}

export interface MatchConfirmationAttachmentState {
  status: ProfilePdfLookupStatusEnum;
  folder: MatchConfirmationFolder | null;
  candidates: MatchConfirmationCandidate[];
}

// AC-16: dates only, deliberately no recipient identity — the DTO omits it, not just this mirror.
export interface MatchConfirmationPriorSend {
  sentOn: string;
}

export interface MatchConfirmationDraft {
  recipients: string[];
  subject: string;
  bodyHtml: string;
  deadlineDate: string;
  attachment: MatchConfirmationAttachmentState;
  // SAA-184 D5 — null for a tenant with no benefits lookup registered (ISC).
  benefitsAttachment: MatchConfirmationAttachmentState | null;
  priorSends: MatchConfirmationPriorSend[];
}

export interface MatchConfirmationAttachmentInput {
  driveFileId?: string;
  fileId?: number;
}

export interface MatchConfirmationStepResult {
  step: MatchConfirmationStepEnum;
  status: MatchConfirmationStepStatusEnum;
  error: string | null;
}

export interface MatchConfirmationReceipt {
  sentAt: string;
  recipientCount: number;
  steps: MatchConfirmationStepResult[];
}

// TipTap mark names the body editor toggles — the whole rich-text contract (SPEC AC-3).
export enum RichTextMarkEnum {
  bold = 'bold',
  italic = 'italic',
  underline = 'underline',
}
